import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';
import { clearLinkedUserId } from '../../utils/persistentUserId';
import DeleteAccountDialog from '../common/DeleteAccountDialog';
import { IRootState } from '../../store/reducer';

const AccountPage: React.FC = () => {
  const { isAuthenticated, isLoading, email, login, logout: auth0Logout } = useAuth();
  const isAccountDeleted = useSelector((s: IRootState) => s.account.isDeleted);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const history = useHistory();

  const handleLogout = () => {
    clearLinkedUserId();
    auth0Logout();
  };

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ bgcolor: '#0f1115', minHeight: '100vh' }}>
        <Container maxWidth="sm" sx={{ pt: 16 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.70)' }} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ bgcolor: '#0f1115', minHeight: '100vh' }}>
        <Container maxWidth="sm" sx={{ pt: 16 }}>
          <Paper
            sx={{
              p: 4,
              bgcolor: '#1c252e',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" sx={{ color: 'white', mb: 2, fontFamily: 'Inter' }}>
              Not logged in
            </Typography>
            <Typography sx={{ color: 'rgba(242,242,243,0.7)', mb: 3, fontFamily: 'Inter' }}>
              Please log in to view your account.
            </Typography>
            <Button
              variant="contained"
              onClick={() => login()}
              sx={{
                bgcolor: '#015ACE',
                color: 'white',
                fontWeight: 700,
                fontFamily: 'Inter',
                textTransform: 'none',
                borderRadius: '8px',
                px: 4,
                '&:hover': {
                  bgcolor: '#0a45c7',
                },
              }}
            >
              Log In
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0f1115', minHeight: '100vh' }}>
      <Container maxWidth="sm" sx={{ pt: 16, pb: 8 }}>
        {!isAccountDeleted && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              color: 'rgba(242,242,243,0.7)',
              fontFamily: 'Inter',
              textTransform: 'none',
              mb: 2,
              ml: -1,
              '&:hover': {
                color: 'white',
                bgcolor: 'rgba(145, 158, 171, 0.08)',
              },
            }}
          >
            Back
          </Button>
        )}
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 700,
            fontFamily: 'Inter',
            mb: 4,
            textAlign: 'center',
          }}
        >
          My Account
        </Typography>

        {isAccountDeleted && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              fontFamily: 'Inter',
              '& .MuiAlert-icon': { color: '#ef4444' },
            }}
          >
            Your account has been deleted. If you believe this was a mistake or would like to restore your account, please contact support.
          </Alert>
        )}

        <Paper
          sx={{
            p: 4,
            bgcolor: '#1c252e',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(242,242,243,0.5)', mb: 0.5, textTransform: 'uppercase', fontSize: 12, fontFamily: 'Inter' }}
            >
              Email
            </Typography>
            <Typography sx={{ color: 'white', fontFamily: 'Inter' }}>
              {email}
            </Typography>
          </Box>

          <Box
            sx={{
              pt: 3,
              borderTop: '1px solid rgba(145, 158, 171, 0.12)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleLogout}
              sx={{
                borderColor: 'rgba(242,242,243,0.2)',
                color: 'white',
                fontWeight: 700,
                fontFamily: 'Inter',
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: 'rgba(242,242,243,0.4)',
                  bgcolor: 'rgba(145, 158, 171, 0.08)',
                },
              }}
            >
              Log Out
            </Button>
            {!isAccountDeleted && (
              <Button
                variant="contained"
                fullWidth
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  fontWeight: 700,
                  fontFamily: 'Inter',
                  textTransform: 'none',
                  borderRadius: '8px',
                }}
              >
                Delete My Account
              </Button>
            )}
          </Box>
        </Paper>
      </Container>

      <DeleteAccountDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
    </Box>
  );
};

export default AccountPage;
